import { baseUrl } from '@utils/utils-config'

export const AUTH_TOAST_QUERY_PARAM = 'authToast'
export const AUTH_SESSION_RECOVERY_QUERY_PARAM = 'sessionRecovery'

/**
 * sessionStorage key for prefilling the login email without leaking it into the
 * URL (query params end up in history, server logs and referrer headers).
 */
export const LOGIN_PREFILL_EMAIL_KEY = 'loginPrefillEmail'

export type AuthToastKind =
  | 'login-success'
  | 'register-success'
  | 'account-deleted'
  | 'password-reset-email-sent'
  | 'password-updated'

export type AuthSessionRecovery = 'failed'

type AuthNavigationContext = {
  checkout?: boolean
  returnTo?: string | null
  sessionRecovery?: AuthSessionRecovery | null
}

const INTERNAL_URL_BASE = 'https://7f.internal'
const CHECKOUT_PATH = '/checkout/'

export function normalizeAuthReturnTo(returnTo?: string | null) {
  if (!returnTo?.startsWith('/') || returnTo.startsWith('//')) {
    return '/'
  }

  return returnTo
}

export function normalizeAuthSessionRecovery(sessionRecovery?: string | null) {
  return sessionRecovery === 'failed' ? 'failed' : null
}

export function shouldAttemptAuthSessionRecovery({
  hasRefreshCookie,
  sessionRecovery,
}: {
  hasRefreshCookie: boolean
  sessionRecovery?: string | null
}) {
  return hasRefreshCookie && normalizeAuthSessionRecovery(sessionRecovery) !== 'failed'
}

export function resolvePostAuthRedirect({ checkout, returnTo }: AuthNavigationContext) {
  const normalizedReturnTo = normalizeAuthReturnTo(returnTo)

  if (normalizedReturnTo !== '/') {
    return normalizedReturnTo
  }

  return checkout ? CHECKOUT_PATH : '/profile/'
}

export function isCheckoutReturnTo(returnTo?: string | null) {
  const normalizedReturnTo = normalizeAuthReturnTo(returnTo)
  return (
    normalizedReturnTo === '/checkout' ||
    normalizedReturnTo === CHECKOUT_PATH ||
    normalizedReturnTo.startsWith(CHECKOUT_PATH)
  )
}

export function isAbsoluteUrl(value: string) {
  return /^https?:\/\//.test(value)
}

export function resolvePasswordResetCompletionRedirect(redirect?: string | null) {
  const normalizedRedirect = redirect?.trim() ?? ''

  if (
    normalizedRedirect === 'checkout' ||
    normalizedRedirect === 'checkout/' ||
    normalizedRedirect === '/checkout' ||
    normalizedRedirect === CHECKOUT_PATH
  ) {
    return CHECKOUT_PATH
  }

  const partnerHubBase = normalizeExternalBase(baseUrl('/partner-hub/'))

  if (partnerHubBase && isAbsoluteUrl(normalizedRedirect)) {
    const normalizedTarget = normalizeExternalBase(normalizedRedirect)

    if (normalizedTarget === partnerHubBase || normalizedTarget.startsWith(`${partnerHubBase}/`)) {
      return normalizedRedirect
    }
  }

  return '/'
}

export function buildAuthPageHref(
  pathname: string,
  { checkout, returnTo, sessionRecovery }: AuthNavigationContext
) {
  const url = toInternalUrl(pathname)
  const normalizedReturnTo = normalizeAuthReturnTo(returnTo)
  const normalizedSessionRecovery = normalizeAuthSessionRecovery(sessionRecovery)

  if (normalizedReturnTo !== '/') {
    url.searchParams.set('returnTo', normalizedReturnTo)
  } else if (checkout) {
    url.searchParams.set('checkout', 'true')
  }

  if (normalizedSessionRecovery) {
    url.searchParams.set(AUTH_SESSION_RECOVERY_QUERY_PARAM, normalizedSessionRecovery)
  }

  return toPathname(url)
}

export function appendAuthToast(pathname: string, toast: AuthToastKind) {
  const url = toInternalUrl(pathname)
  url.searchParams.set(AUTH_TOAST_QUERY_PARAM, toast)
  return toPathname(url)
}

export function removeAuthToast(pathname: string) {
  const url = toInternalUrl(pathname)
  url.searchParams.delete(AUTH_TOAST_QUERY_PARAM)
  return toPathname(url)
}

export function getAuthToastMessage(toast: string | null) {
  switch (toast) {
    case 'login-success':
      return 'Das hat geklappt. Du bist jetzt angemeldet.'
    case 'password-reset-email-sent':
      return 'Die E-Mail wurde versandt.'
    case 'password-updated':
      return 'Dein Passwort wurde aktualisiert.'
    case 'register-success':
      return 'Danke für dein Vertrauen! Du bist erfolgreich registriert und angemeldet.'
    case 'account-deleted':
      return 'Dein Konto wurde gelöscht.'
    case null:
    default:
      return null
  }
}

function toInternalUrl(pathname: string) {
  return new URL(pathname, INTERNAL_URL_BASE)
}

function toPathname(url: URL) {
  return `${url.pathname}${url.search}${url.hash}`
}

function normalizeExternalBase(url?: string | null) {
  return url?.trim().replace(/\/+$/, '') ?? ''
}
